import { Component, DestroyRef, Directive, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@examdojo/core/button';
import { InternationalizationService } from '@examdojo/core/i18n';
import { ToastService } from '@examdojo/toast';
import { ensureArray } from '@examdojo/util/ensure-array';
import { TranslocoPipe } from '@jsverse/transloco';
import { OrArray } from '@ngneat/elf';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { combineLatest, Observable, tap } from 'rxjs';
import { ImageComponent } from '../image/image.component';
@Directive()
export class _ImageDropzoneComponent<T = File | File[]> {
  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly toastService: ToastService,
    private readonly internationalizationService: InternationalizationService,
  ) {}

  readonly accept = input('image/*');
  readonly hideIcon = input<boolean>(false);
  readonly multiple: boolean = false;
  readonly imageSelected = output<T | null>();

  dropped(event: NgxFileDropEntry[]): void {
    if (!this.multiple) {
      const droppedFileEntry = event[0];

      if (droppedFileEntry && droppedFileEntry.fileEntry.isFile) {
        const fileEntry = droppedFileEntry.fileEntry as FileSystemFileEntry;

        this.fileToObservable(fileEntry)
          .pipe(
            tap((file) => {
              if (!this.isFileTypeAccepted(file)) {
                this.warnRejectedFileType(file);
                return;
              }
              this.imageSelected.emit(file as T);
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    } else {
      combineLatest(
        event
          .filter((entry) => entry && entry.fileEntry.isFile)
          .map((entry) => this.fileToObservable(entry.fileEntry as FileSystemFileEntry)),
      )
        .pipe(
          tap((files) => {
            const { accepted, rejected } = files.reduce<{ accepted: File[]; rejected: File[] }>(
              (acc, file) => {
                if (this.isFileTypeAccepted(file)) {
                  acc.accepted.push(file);
                } else {
                  acc.rejected.push(file);
                }
                return acc;
              },
              { accepted: [], rejected: [] },
            );

            if (rejected.length) {
              this.warnRejectedFileType(rejected);
            }

            if (accepted.length) {
              this.imageSelected.emit(accepted as T);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  private fileToObservable(fileEntry: FileSystemFileEntry): Observable<File> {
    return new Observable<File>((observer) => {
      fileEntry.file(
        (file) => {
          observer.next(file);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  private isFileTypeAccepted(file: File): boolean {
    return this.accept().includes(file.type);
  }

  private warnRejectedFileType(files: OrArray<File>) {
    ensureArray(files).forEach((file) => {
      const message = this.internationalizationService.translate('examdojo.not_accepted_file_type', {
        fileName: file.name,
      });
      this.toastService.warning(message);
    });
  }
}

@Component({
  selector: 'dojo-image-dropzone',
  templateUrl: './image-dropzone.component.html',
  styleUrl: 'image-dropzone.component.scss',
  imports: [NgxFileDropModule, TranslocoPipe, ImageComponent, ButtonComponent],
})
export class ImageDropzoneComponent extends _ImageDropzoneComponent<File> {}

@Component({
  selector: 'dojo-multiple-image-dropzone',
  templateUrl: './image-dropzone.component.html',
  styleUrl: 'image-dropzone.component.scss',
  imports: [NgxFileDropModule, TranslocoPipe, ImageComponent, ButtonComponent],
})
export class MultipleImageDropzoneComponent extends _ImageDropzoneComponent<File[]> {
  override readonly multiple = true;
}
